/*
  Gnü gift card purchase form.

  Shopify has no free-text gift card amount: every denomination is a variant.
  The product carries one variant per $5 step, the picker is hidden, and this
  file maps whatever the buyer taps or types onto the matching variant id.

  The recipient side is left alone: email, name, message, send-on date, and the
  browser timezone offset that decides which local day "send on" means all stay
  with Shopify's own <recipient-form> in recipient-form.js. This only flips its
  checkbox. Keeping that plumbing native is what makes scheduled send Shopify's
  job rather than ours, and it survives a Dawn upgrade.
*/

var GNU_GC_DEBUG = true;
function gnuGcDebug() {
  if (!GNU_GC_DEBUG) return;
  var args = ['[gnu-gift-card]'];
  for (var i = 0; i < arguments.length; i++) args.push(arguments[i]);
  try {
    console.log.apply(console, args);
  } catch (err) {}
}

if (!customElements.get('gnu-gift-card')) {
  customElements.define(
    'gnu-gift-card',
    class GnuGiftCard extends HTMLElement {
      connectedCallback() {
        const id = this.dataset.sectionId;

        // Sorted rather than trusted: min/max and the snapping both index off the
        // ends, and variant order follows however the denominations were entered.
        this.variants = JSON.parse(this.querySelector('[data-gc-variants]').textContent).sort(
          (a, b) => a.amount - b.amount
        );
        if (!this.variants.length) return;

        this.step = Number(this.dataset.step) || 5;
        this.min = this.variants[0].amount;
        this.max = this.variants[this.variants.length - 1].amount;

        this.variantIdInput = this.querySelector('[data-gc-variant-id]');
        this.chips = Array.from(this.querySelectorAll('[data-gc-amount]'));
        this.customToggle = this.querySelector('[data-gc-custom-toggle]');
        this.customWrap = this.querySelector('[data-gc-custom]');
        this.customInput = this.querySelector('[data-gc-custom-input]');
        this.amountHint = this.querySelector('[data-gc-amount-hint]');
        this.submit = this.querySelector('[data-gc-submit]');

        // The card preview sits in the other grid column, outside this element,
        // so it has to be reached from the section root rather than from `this`.
        // Scoped to .gnu-gc so two sections on one page keep their own cards.
        const root = this.closest('.gnu-gc') || document;
        this.faceAmount = root.querySelector('[data-gc-face-amount]');
        this.faceTo = root.querySelector('[data-gc-face-to]');
        this.faceCard = root.querySelector('[data-gc-face]');
        this.faceArt = root.querySelector('[data-gc-face-art]');
        this.designButtons = Array.from(this.querySelectorAll('[data-gc-design]'));
        this.designNameInput = this.querySelector('[data-gc-design-name]');

        this.modeButtons = Array.from(this.querySelectorAll('[data-gc-mode]'));
        this.giftFields = this.querySelector('[data-gc-gift-fields]');
        this.whenButtons = Array.from(this.querySelectorAll('[data-gc-when]'));
        this.dateWrap = this.querySelector('[data-gc-date]');

        // Owned by recipient-form.js; we only read and nudge these.
        this.recipientCheckbox = this.querySelector(`#Recipient-checkbox-${id}`);
        this.recipientName = this.querySelector(`#Recipient-name-${id}`);
        this.recipientEmail = this.querySelector(`#Recipient-email-${id}`);
        this.messageInput = this.querySelector(`#Recipient-message-${id}`);
        this.sendOnInput = this.querySelector(`#Recipient-send-on-${id}`);

        // Ours, not part of the native contract.
        this.fromInput = this.querySelector('[data-gc-from]');

        this.counter = this.querySelector('[data-gc-counter]');

        this.bindAmounts();
        this.bindDesigns();
        this.bindMode();
        this.bindSchedule();
        this.bindMessageCounter();
        this.bindFace();
        this.bindCartReset();

        this.selectAmount(Number(this.dataset.defaultAmount) || this.variants[0].amount, { fromChip: true });
        if (this.designButtons.length) this.selectDesign(this.designButtons[0]);
        this.setMode('gift');
        this.setWhen('now');
        gnuGcDebug('ready', {
          variants: this.variants.length,
          designs: this.designButtons.length,
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      }

      /*
        On a successful add, recipient-form.js clears its inputs and unchecks its
        own checkbox, and it does that without firing a change event, so
        nothing tells our segmented control. Left alone the buttons would still
        read "Someone else" while the checkbox underneath said otherwise, and
        the next add would quietly go through as a plain purchase, not a gift.

        Re-asserting the mode after the reset gives a blank gift form instead.
        Deferred by a task because recipient-form.js is subscribed to the same
        event and has to finish clearing first.
      */
      bindCartReset() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source !== 'product-form') return;
          setTimeout(() => {
            this.setMode(this.mode);
            this.setWhen('now');
            this.paintTo();
            this.messageInput?.dispatchEvent(new Event('input'));
          }, 0);
        });
      }

      /* ---------- amount ---------- */

      bindAmounts() {
        this.chips.forEach((chip) => {
          chip.addEventListener('click', () => {
            this.selectAmount(Number(chip.dataset.gcAmount), { fromChip: true });
          });
        });

        this.customToggle?.addEventListener('click', () => {
          this.showCustom();
        });

        // Live face update while typing, snap once they stop.
        this.customInput?.addEventListener('input', () => {
          const raw = Number(this.customInput.value);
          if (Number.isFinite(raw) && raw > 0) this.paintFace(raw);
        });

        this.customInput?.addEventListener('change', () => this.commitCustom());
        this.customInput?.addEventListener('blur', () => this.commitCustom());
      }

      commitCustom() {
        const raw = Number(this.customInput.value);

        if (!Number.isFinite(raw) || raw <= 0) {
          this.customInput.value = '';
          this.setHint(`Enter an amount between ${this.money(this.min)} and ${this.money(this.max)}.`, true);
          this.setSubmitEnabled(false);
          return;
        }

        const clamped = Math.min(Math.max(raw, this.min), this.max);
        const snapped = Math.round(clamped / this.step) * this.step;

        this.customInput.value = snapped;
        this.selectAmount(snapped, { fromChip: false });

        if (raw > this.max) {
          this.setHint(`${this.money(this.max)} is the most we can send in one card.`, true);
        } else if (raw < this.min) {
          this.setHint(`${this.money(this.min)} is our smallest card.`, true);
        } else if (snapped !== raw) {
          this.setHint(`Rounded to ${this.money(snapped)}. Cards come in ${this.money(this.step)} steps.`, false);
        } else {
          this.setHint('', false);
        }
      }

      // Nearest rather than exact: a preset configured in the theme editor, or a
      // step that has drifted from the product's denominations, would otherwise
      // select nothing and leave the form unsubmittable.
      nearestVariant(amount) {
        return this.variants.reduce((best, v) =>
          Math.abs(v.amount - amount) < Math.abs(best.amount - amount) ? v : best
        );
      }

      selectAmount(amount, { fromChip }) {
        const variant = this.nearestVariant(amount);
        if (!variant) return;

        this.amount = variant.amount;
        this.variantIdInput.value = variant.id;

        const matchingChip = this.chips.find((c) => Number(c.dataset.gcAmount) === variant.amount);
        this.chips.forEach((c) => c.setAttribute('aria-pressed', String(c === matchingChip)));
        this.customToggle?.setAttribute('aria-pressed', String(!matchingChip));

        if (fromChip && matchingChip) {
          this.hideCustom();
          this.setHint('', false);
        }

        this.paintFace(variant.amount);
        this.setSubmitEnabled(true);

        // recipient-form.js tracks the variant it is attached to so it can match
        // cart errors and reset itself after a successful add. Without this it
        // would still be watching the first denomination.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.dataset.sectionId, variant: { id: variant.id } },
          });
        }
      }

      showCustom() {
        this.customWrap.hidden = false;
        this.chips.forEach((c) => c.setAttribute('aria-pressed', 'false'));
        this.customToggle?.setAttribute('aria-pressed', 'true');
        this.customInput.focus();
        if (!this.customInput.value) this.setSubmitEnabled(false);
      }

      hideCustom() {
        this.customWrap.hidden = true;
        this.customInput.value = '';
      }

      setHint(text, isWarning) {
        if (!this.amountHint) return;
        this.amountHint.textContent = text;
        this.amountHint.classList.toggle('gnu-gc__hint--warn', Boolean(isWarning));
      }

      setSubmitEnabled(enabled) {
        this.submit.disabled = !enabled;
      }

      /* ---------- card design ---------- */

      bindDesigns() {
        if (!this.designButtons.length) return;
        this.designButtons.forEach((button) => {
          button.addEventListener('click', () => this.selectDesign(button));
        });
      }

      selectDesign(button) {
        const name = button.dataset.gcDesignName || '';
        const art = button.dataset.gcDesignArt || '';
        const color = button.dataset.gcDesignColor || '#FFFFFF';

        this.designButtons.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));

        if (this.designNameInput) this.designNameInput.value = name;

        if (this.faceCard) {
          this.faceCard.style.setProperty('--gc-type', color);
          this.faceCard.style.color = color;
        }

        if (this.faceArt && art) {
          this.faceArt.setAttribute('href', art);
          this.faceArt.setAttributeNS('http://www.w3.org/1999/xlink', 'href', art);
        }

        gnuGcDebug('design', { name: name, color: color, art: art });
      }

      /* ---------- for me / for someone else ---------- */

      bindMode() {
        this.modeButtons.forEach((button) => {
          button.addEventListener('click', () => this.setMode(button.dataset.gcMode));
        });
      }

      setMode(mode) {
        const gifting = mode === 'gift';
        this.mode = mode;

        this.modeButtons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.gcMode === mode)));
        this.giftFields.hidden = !gifting;

        // recipient-form.js listens for a bubbling change on its own subtree and
        // enables or disables (and clears) every recipient input off this one
        // checkbox, which is also what tells Shopify to treat the line as a gift.
        if (this.recipientCheckbox.checked !== gifting) {
          this.recipientCheckbox.checked = gifting;
          this.recipientCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // recipient-form.js clears and disables its own four off that change.
        // This one it has never heard of: inputFields() is a fixed list. Left
        // alone it stays in the form while hidden, so a card bought for
        // yourself would ship with a From on it.
        if (this.fromInput) {
          this.fromInput.disabled = !gifting;
          if (!gifting) this.fromInput.value = '';
        }

        if (gifting) {
          this.recipientEmail.required = true;
        } else {
          this.recipientEmail.required = false;
          this.setWhen('now');
        }

        this.paintTo();
      }

      /* ---------- send now / schedule ---------- */

      bindSchedule() {
        this.whenButtons.forEach((button) => {
          button.addEventListener('click', () => this.setWhen(button.dataset.gcWhen));
        });

        if (this.sendOnInput) {
          // Bounds in the buyer's own local time. Shopify pairs the date with the
          // captured __shopify_offset, so "Dec 25" is their Dec 25, not UTC's.
          //
          // 90 days is Shopify's own ceiling on Send on, not a choice made here.
          // Offering anything past it would only fail at checkout.
          const today = new Date();
          const iso = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
          const latest = new Date(today);
          latest.setDate(latest.getDate() + 90);

          this.sendOnInput.min = iso(today);
          this.sendOnInput.max = iso(latest);
        }
      }

      setWhen(when) {
        const later = when === 'later';
        this.whenButtons.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.gcWhen === when)));
        this.dateWrap.hidden = !later;
        if (!later && this.sendOnInput) this.sendOnInput.value = '';
      }

      /* ---------- message counter ---------- */

      bindMessageCounter() {
        if (!this.messageInput || !this.counter) return;
        const max = this.messageInput.getAttribute('maxlength') || 200;
        const paint = () => {
          this.counter.textContent = `${this.messageInput.value.length} / ${max}`;
        };
        this.messageInput.addEventListener('input', paint);
        paint();
      }

      /* ---------- card face ---------- */

      bindFace() {
        this.recipientName?.addEventListener('input', () => this.paintTo());
      }

      paintFace(amount) {
        if (this.faceAmount) this.faceAmount.textContent = this.money(amount);
      }

      paintTo() {
        if (!this.faceTo) return;
        const gifting = this.recipientCheckbox.checked;
        const name = this.recipientName?.value.trim();
        this.faceTo.textContent = gifting && name ? `for ${name}` : '';
      }

      money(amount) {
        return `$${Number(amount).toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`;
      }
    }
  );
}
